<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import type { DropdownMenuItem, DropdownMenuProps } from './dropdown-menu';
import { ref } from 'vue';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import IconButton from '../IconButton/IconButton.vue';
import ProFeatureBadge from '../ProFeatureBadge/ProFeatureBadge.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<DropdownMenuProps>(), {
  appearance: 'default',
  triggerSize: 'sm',
  triggerVariant: 'ghost-neutral',
});

const DROPDOWN_OFFSET = 4;
const VIEWPORT_PADDING = 8;
const MIN_AVAILABLE_HEIGHT = 0;

const menuButtonRef = ref<HTMLElement | null>(null);
const menuItemsRef = ref<HTMLElement | null>(null);
const { floatingStyles } = useFloating(menuButtonRef, menuItemsRef, {
  placement: props.placement,
  whileElementsMounted: autoUpdate,
  strategy: 'fixed',
  middleware: [
    offset(DROPDOWN_OFFSET),
    flip(),
    shift({ padding: VIEWPORT_PADDING }),
    size({
      padding: VIEWPORT_PADDING,
      apply({ availableHeight, elements }): void {
        const maxHeight = Math.max(MIN_AVAILABLE_HEIGHT, availableHeight);
        elements.floating.style.maxHeight = `${String(maxHeight)}px`;
        elements.floating.style.overflowY = 'auto';
      },
    }),
  ],
});

const shouldShowGroupLabel = (index: number): boolean => {
  const groupLabel = props.items[index]?.groupLabel;

  return !!groupLabel && props.items[index - 1]?.groupLabel !== groupLabel;
};

// The first item has nothing above it to be separated from, and a group label
// already draws its own divider.
const shouldShowSeparator = (index: number): boolean =>
  !!props.items[index]?.separated && index > 0 && !shouldShowGroupLabel(index);

/**
 * Headless UI's MenuItem closes the menu from its own click handler. The shared
 * render helper merges listeners child-first and skips the rest of the chain
 * once one has called `preventDefault()`, so preventing here suppresses that
 * close while leaving MenuItem in place for registration and active styling.
 */
const onItemClick = (item: DropdownMenuItem, event: MouseEvent): void => {
  if (item.keepOpen) {
    event.preventDefault();
  }
  item.onClick?.();
};

/**
 * Weight lives here rather than in the shared base classes so a 'subtle' item
 * can step down from the default semibold without fighting it.
 */
const getItemClasses = (item: DropdownMenuItem, active: boolean): string => {
  if (item.type === 'danger') {
    return active
      ? 'font-semibold bg-danger-50 text-danger-600'
      : 'font-semibold text-danger-600 hover:bg-danger-50';
  }

  if (item.type === 'subtle') {
    return active
      ? 'font-normal bg-neutral-100 text-neutral-600'
      : 'font-normal text-neutral-500 hover:bg-neutral-50';
  }

  return active
    ? 'font-semibold bg-primary-50 text-primary-600'
    : 'font-semibold text-neutral-700 hover:bg-neutral-50';
};
</script>

<template>
  <Menu v-slot="{ open, close }">
    <MenuButton ref="menuButtonRef" as="div">
      <!--
        Default trigger: the standard ⋯ overflow button. Override it by passing
        your own trigger into the default slot.
      -->
      <slot>
        <IconButton
          :size="props.triggerSize"
          icon="ellipsis"
          :variant="props.triggerVariant"
          data-testid="ellipsis-menu-button"
        />
      </slot>
    </MenuButton>
    <Teleport v-if="open" to="body">
      <!--
        Full-screen catcher: the first click outside the menu lands here, which
        closes the menu and is swallowed — so it does not also trigger an
        adjacent control underneath (e.g. when the menu is open above a modal).
        Mirrors the behaviour of TContextActionMenu.
      -->
      <div class="pointer-events-none fixed inset-0 z-9999">
        <MenuItems
          ref="menuItemsRef"
          :style="floatingStyles"
          class="pointer-events-auto flex max-w-xs min-w-64 origin-top-right flex-col overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-2 shadow-2xl focus-visible:outline-none"
          :class="props.appearance === 'editor' ? 'w-80 gap-1' : 'w-max gap-2'"
        >
          <!--
            Optional prominent header above the items (e.g. a primary action),
            followed by a divider when there are also items. Pass `close` so the
            header content can dismiss the menu after acting.
          -->
          <template v-if="$slots.header">
            <slot name="header" :close="close" />
            <div
              v-if="props.items.length"
              class="border-t border-neutral-200"
            ></div>
          </template>
          <template
            v-for="(item, index) in props.items"
            :key="`${item.label}-${index}`"
          >
            <div
              v-if="shouldShowGroupLabel(index)"
              :class="[
                'px-2 pt-1 text-xs font-semibold tracking-wider text-neutral-500 uppercase',
                index > 0 && 'mt-1 border-t border-neutral-200 pt-3',
              ]"
            >
              {{ item.groupLabel }}
            </div>
            <div
              v-else-if="shouldShowSeparator(index)"
              class="border-t border-neutral-200"
              :class="props.appearance === 'editor' && 'my-2'"
              role="separator"
            ></div>
            <MenuItem
              v-slot="{ active }"
              :role="
                item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'
              "
              :aria-checked="item.checked"
            >
              <button
                :class="[
                  'flex w-full items-center gap-2 text-left text-sm transition',
                  props.appearance === 'editor'
                    ? 'rounded-xl px-4 py-2'
                    : 'rounded-md px-2 py-1',
                  getItemClasses(item, active),
                ]"
                @click="onItemClick(item, $event)"
              >
                <span
                  v-if="item.indicatorClass"
                  class="h-3 w-5 shrink-0 rounded-full border"
                  :class="item.indicatorClass"
                />
                <HugeIcon
                  v-if="item.icon"
                  :class="item.iconClass"
                  :icon="item.icon"
                />
                <ProFeatureBadge
                  v-if="item.showProBadge"
                  placement="corner"
                  class="dropdown-menu-pro-badge min-w-0 flex-1"
                >
                  <!-- `pr-6` reserves the space the overlaid crown sits in, so it
                       never covers the tail of a label. -->
                  <span class="block pr-6">{{ item.label }}</span>
                  <span
                    v-if="item.description"
                    class="block pr-6 text-xs font-normal text-slate-500"
                    >{{ item.description }}</span
                  >
                </ProFeatureBadge>
                <span v-else class="min-w-0 flex-1">
                  <span class="block">{{ item.label }}</span>
                  <span
                    v-if="item.description"
                    class="block text-xs font-normal text-slate-500"
                    >{{ item.description }}</span
                  >
                </span>
                <HugeIcon
                  v-if="item.trailingIcon"
                  :class="['ml-auto shrink-0', item.trailingIconClass]"
                  :icon="item.trailingIcon"
                  :size="16"
                />
                <span
                  v-if="item.checked !== undefined"
                  aria-hidden="true"
                  class="ml-auto flex w-8 shrink-0 justify-end"
                >
                  <HugeIcon v-if="item.checked" icon="checkmark" />
                </span>
              </button>
            </MenuItem>
          </template>
        </MenuItems>
      </div>
    </Teleport>
  </Menu>
</template>

<style scoped>
/*
  The menu item is only `py-1` tall, so the badge's default corner offset would
  push the crown above the item's top edge — and clip it once the menu scrolls.
  Tuck it in beside the label instead.

  `translate: none` is load-bearing: Tailwind v4 emits `-translate-y-3/4` via the
  standalone `translate` property, so overriding `transform` does nothing here.
*/
.dropdown-menu-pro-badge :deep(> .absolute) {
  top: 0.125rem;
  right: 0.25rem;
  left: auto;
  translate: none;
}
</style>
